import React, {useEffect, useMemo, useState} from 'react';
import merge from 'lodash/merge';
import Chart from 'react-apexcharts';
import {Report, ReportStreamBpmLog} from '../../../../types/Api/Reports';
import moment from 'moment';
import {IsoDate} from '../../../../types/internals';
import {DEFAULT_REPORTS_CHART_CONFIG} from './ReportsV2ViewChart.constants';

export type ReportsV2ViewChartProps = {
  reportBpmLogs: ReportStreamBpmLog[];
  report?: Report;
  onPickTime: (time: number) => void;
};

type BpmLogsGroup = {
  startTime: IsoDate;
  endTime: IsoDate;
  maxBpm: number;
  avgBpm: number;
  items: ReportStreamBpmLog[];
};

const calculateMaxBpm = (logs: ReportStreamBpmLog[]) => {
  return logs.reduce((max, log) => (log.bpm > max ? log.bpm : max), 0);
};
const calculateAvgBpm = (logs: ReportStreamBpmLog[]) => {
  return Math.round(logs.reduce((sum, log) => sum + log.bpm, 0) / logs.length);
};

const aggregateOverviewLogs = ({
  bpmLogs,
  aggregateAmount = 25,
}: {
  bpmLogs: ReportStreamBpmLog[];
  aggregateAmount?: number;
}): BpmLogsGroup[] => {
  const groupLength = Math.ceil(bpmLogs.length / aggregateAmount);
  const groupedArr: BpmLogsGroup[] = [];

  for (
    let groupN = 0;
    groupN < Math.min(aggregateAmount, bpmLogs.length);
    groupN++
  ) {
    const items = bpmLogs.slice(
      groupN * groupLength,
      (groupN + 1) * groupLength
    );
    groupedArr.push({
      startTime: items[0]?.createdAt,
      endTime: items[items.length - 1]?.createdAt,

      items,
      maxBpm: calculateMaxBpm(items),
      avgBpm: calculateAvgBpm(items),
    });
  }

  return groupedArr;
};

const formatTime = ({
  timestamp,
  startTimestamp,
  withSeconds = false,
}: {
  timestamp: number;
  startTimestamp: number;
  withSeconds?: boolean;
}) => {
  const duration = moment.duration(timestamp - startTimestamp);

  const hm = `${duration.hours()}:${duration
    .minutes()
    .toString()
    .padStart(2, '0')}`;

  if (withSeconds) {
    return `${hm}:${duration.seconds().toString().padStart(2, '0')}`;
  }

  return hm;
};

const groupHeartRateData = (
  data: ReportStreamBpmLog[],
  groupLength = 100
): {x: number; y: number; meta: {time: number}}[] => {
  if (!data.length) return [];

  // Index starts from 1 to improve chart appearance
  let itemIndex = 1;
  const itemsLength = Math.ceil(data.length / groupLength);
  const aggr: {x: number; y: number; meta: {time: number}}[] = [];
  let max = 0;
  let time: string;

  for (let i = 0; i < data.length; i++) {
    if (data[i].bpm > max) {
      max = data[i].bpm;
      time = data[i].createdAt;
    }

    if (i % itemsLength === itemsLength - 1) {
      aggr.push({
        x: itemIndex++,
        y: max,
        meta: {time: time!},
      } as any);
      max = 0;
    }
  }
  return aggr;
};

export const ReportsV2ViewChart = ({
  reportBpmLogs,
  report,
  onPickTime,
}: ReportsV2ViewChartProps) => {
  const aggregatedLogs = aggregateOverviewLogs({bpmLogs: reportBpmLogs});
  const [selectedGroup, setSelectedGroup] = useState<BpmLogsGroup>();
  const detailedItems = useMemo(
    () =>
      selectedGroup?.items ? groupHeartRateData(selectedGroup?.items) : [],
    [selectedGroup]
  );
  const startTime = moment(report?.from).valueOf();
  const maxBpm = useMemo(
    () =>
      aggregatedLogs.length > 0
        ? aggregatedLogs.reduce((maxLog, log) =>
            log.maxBpm > maxLog.maxBpm ? log : maxLog
          ).maxBpm
        : 0,
    [aggregatedLogs]
  );

  // Initialize selectedGroup with Max aggregated group
  useEffect(() => {
    if (!selectedGroup && aggregatedLogs.length > 0) {
      setSelectedGroup(
        aggregatedLogs.reduce((maxLog, log) =>
          log.maxBpm > maxLog.maxBpm ? log : maxLog
        )
      );
    }
  }, [aggregatedLogs, selectedGroup]);

  const top10AggregateThreshold =
    [...aggregatedLogs.sort((a, b) => b.maxBpm - a.maxBpm)][1]?.maxBpm || 0;

  return (
    <>
      <Chart
        height={200}
        options={merge({}, DEFAULT_REPORTS_CHART_CONFIG, {
          chart: {
            id: 'chartOverview',
            events: {
              dataPointSelection: (_event, _ctx, {dataPointIndex}) => {
                // Hack that fix apex charts layout issue
                // https://github.com/apexcharts/apexcharts.js/issues/1790#issuecomment-1021941450
                setTimeout(
                  () => setSelectedGroup(aggregatedLogs[dataPointIndex]),
                  10
                );
              },
            },
          },
          colors: [
            ({value, dataPointIndex}) => {
              if (
                selectedGroup?.startTime ===
                aggregatedLogs[dataPointIndex]?.startTime
              ) {
                return '#9696A3';
              }

              return value >= top10AggregateThreshold ? '#07aba1' : '#51515c';
            },
          ],
          xaxis: {
            labels: {
              formatter: (time) =>
                formatTime({
                  timestamp: new Date(time).getTime(),
                  startTimestamp: startTime,
                }),
              style: {colors: '#6E6E78'},
            },
            tickAmount: 6,
          },
          yaxis: {tickAmount: 3},
          states: {
            active: {
              filter: {type: 'none'},
            },
          },
        })}
        type="bar"
        series={[
          {
            name: 'Max BPM',
            data: aggregatedLogs.map((data) => [
              new Date(data.startTime).getTime(),
              data.maxBpm,
            ]) as any,
          },
        ]}
      />

      <Chart
        height={200}
        options={merge({}, DEFAULT_REPORTS_CHART_CONFIG, {
          chart: {
            id: 'chartDetailed',
            events: {
              dataPointSelection: (_event, _ctx, {dataPointIndex}) => {
                const time = detailedItems[dataPointIndex]?.meta?.time;
                onPickTime(time);
              },
            },
          },
          xaxis: {
            ...DEFAULT_REPORTS_CHART_CONFIG.xaxis,
            labels: {
              formatter: (x: number) =>
                formatTime({
                  timestamp: new Date(
                    // Index starts from 1 to improve chart appearance
                    detailedItems[x - 1]?.meta?.time
                  ).getTime(),
                  startTimestamp: startTime,
                  withSeconds: true,
                }),
              style: {colors: '#6E6E78'},
            },
          },
          yaxis: {
            ...DEFAULT_REPORTS_CHART_CONFIG.yaxis,
            max: maxBpm,
            labels: {show: false},
          },
          colors: ['#51515c'],
        })}
        type="bar"
        series={[{name: 'BPM', data: detailedItems}]}
      />
    </>
  );
};
